// React
import PropTypes from "prop-types";
import { act, useContext, useEffect, useState } from "react";

// @mui material components
import {
  Autocomplete,
  Card,
  Checkbox,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Modal,
  TextField,
  Tooltip,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import { gql, useLazyQuery, useMutation } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "70%" },
  overflowY: "auto",
  p: 3,
};
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
export default function ModalEmpresa({ open, handleClose, refetch, idEmpresa }) {
  const [empresa, setEmpresa] = useState({});
  const [actividades, setActividades] = useState([]);
  const { handleSnackbar } = useContext(MessageManager);

  const [getEmpresa, { data: dataEmpresa }] = useLazyQuery(
    gql`
      query getEmpresa($id: ID!) {
        empresa(id: $id) {
          __typename @skip(if: true)
          id
          razonSocial
          cuit
          email
          actividad
          gerente
          codigoAfip
          registroCatastral
          habilitacionMunicipalNumero
          habilitacionMunicipalLugar
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
    }
  );
  const [saveEmpresa, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveEmpresa($input: EmpresaInput!) {
        saveEmpresa(input: $input) {
          id
        }
      }
    `
  );

  useEffect(() => {
    if (idEmpresa && open) {
      getEmpresa({ variables: { id: idEmpresa } })
        .then((res) => {
          setActividades(res?.data?.empresa?.actividad || []);
        })
        .catch((e) => {
          handleSnackbar(e.message || "Error al obtener la empresa", "error");
        });
    }
  }, [idEmpresa, open]);

  useEffect(() => {
    if (dataEmpresa && idEmpresa && open) {
      setEmpresa(dataEmpresa.empresa);
    } else {
      setEmpresa({});
    }
  }, [dataEmpresa, idEmpresa, open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftTypography variant="h6">Datos empresa</SoftTypography>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
            <SoftBox py={3}>
              <Grid container spacing={2}>
                {/* Existing fields */}
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="razonSocial">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Razón Social
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="body2">
                    {empresa?.razonSocial || "Sin información"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="cuit">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      CUIT
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="body2">
                    {empresa?.cuit || "Sin información"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="email">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Email
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="body2">
                    {empresa?.email || "Sin información"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="actividad">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Actividad
                    </SoftTypography>
                  </InputLabel>
                  <Autocomplete
                    multiple
                    id="actividad"
                    size="small"
                    options={[
                      "Transportista",
                      "Generadora",
                      "Generador Comercio Servicio",
                      // "Generadora/Operadora",
                      "Operadora",
                      "Operador in situ",
                      "Recicladora/Operadora",
                      // "Recicladora",
                    ]}
                    disabled
                    disableCloseOnSelect
                    getOptionLabel={(option) => option}
                    value={actividades}
                    onChange={(event, newValue) => {
                      const updatedActividades = newValue.filter(
                        (item, index, self) => self.findIndex((t) => t === item) === index
                      );
                      setActividades(updatedActividades);
                    }}
                    renderOption={(props, option, { selected }) => {
                      return (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={actividades.some((actividad) => actividad === option)}
                          />
                          {option}
                        </li>
                      );
                    }}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="gerente">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Gerente
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="body2">
                    {empresa?.gerente || "Sin información"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="codigoAfip">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Código AFIP
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="body2">
                    {empresa?.codigoAfip || "Sin información"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="registroCatastral">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Registro Catastral
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="body2">
                    {empresa?.registroCatastral || "Sin información"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="habilitacionMunicipalNumero">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Habilitación Municipal Número
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="body2">
                    {empresa?.habilitacionMunicipalNumero || "Sin información"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="habilitacionMunicipalLugar">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Habilitación Municipal Lugar
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="body2">
                    {empresa?.habilitacionMunicipalLugar || "Sin información"}
                  </SoftTypography>
                </Grid>
              </Grid>
            </SoftBox>
            <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
              <SoftButton color="primary" onClick={handleClose} sx={{ marginRight: 1 }}>
                <Icon sx={{ fontWeight: "light" }}>close</Icon>
                &nbsp;Cerrar
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalEmpresa.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  idEmpresa: PropTypes.string,
};
